import type { Session } from '@riskpay/ui-components';

/* ==========================================================================
 *  Staff handover
 *
 *  How a session minted by the Quantum panel's "open dashboard" gets into this
 *  app.
 *
 *  It arrives in the URL fragment, not the query string, and that is the whole
 *  point of the design: a fragment is never sent to a server, so the token does
 *  not appear in access logs, proxy logs, or a Referer header on the first
 *  outbound request. It is removed from the address bar the moment it is read,
 *  so it does not survive into browser history or a shared screenshot either.
 *
 *  Nothing here grants anything. The token was signed by the API and carries
 *  the staff member's identity inside it; this only moves it from the URL into
 *  the session store. Forging the descriptive fields changes the wording of a
 *  banner and nothing about what the API will allow.
 * ========================================================================== */

const FRAGMENT_KEY = 'staff=';

interface HandoverPayload {
  token: string;
  mode?: 'TEST' | 'LIVE';
  user: { id: string; email: string; role: string; merchant_id?: string };
  staff: { email: string; role: string; merchant: string; actingAs: string };
}

/** Base64url → string, tolerant of the padding the encoder omits. */
function decodeFragment(raw: string): string {
  const padded = raw.replace(/-/g, '+').replace(/_/g, '/');
  return decodeURIComponent(escape(atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))));
}

/**
 * Reads a handover out of the URL, if one is there, and clears it.
 *
 * Returns null for the overwhelmingly common case of a normal page load, so
 * the caller can treat a handover as the exception it is.
 */
export function readStaffHandover(): Session | null {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash.startsWith(FRAGMENT_KEY)) return null;

  // Cleared first, and unconditionally. A malformed payload must not leave a
  // token sitting in the address bar because parsing happened to throw.
  const raw = hash.slice(FRAGMENT_KEY.length);
  window.history.replaceState(null, '', window.location.pathname + window.location.search);

  try {
    const payload = JSON.parse(decodeFragment(raw)) as HandoverPayload;
    if (!payload?.token || !payload?.user?.email) return null;

    return {
      accessToken: payload.token,
      user: payload.user,
      mode: payload.mode ?? 'TEST',
      staff: payload.staff,
    };
  } catch {
    // A fragment we cannot read is not worth a message to the user — they did
    // not type it. The staff member will see the ordinary sign-in screen.
    return null;
  }
}

/** Builds the fragment. Lives here so both ends share one format. */
export function buildStaffHandover(payload: HandoverPayload): string {
  const json = JSON.stringify(payload);
  const b64 = btoa(unescape(encodeURIComponent(json)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return `#${FRAGMENT_KEY}${b64}`;
}
